// The statement checkup's logic, without React: which statements it walks,
// how a check's items group into changes, what is selected by default and
// what a click on Apply sends. Every function is pure.

// The run statuses whose items can still be decided and applied.
export const DECIDABLE_RUNS = ['measured', 'approved', 'applying'];

// What a change is, as `item.change.kind` names it, in the order the review
// lists the groups. The first four are a row read differently; `leftover` is
// a bank row an earlier import of the statement left in a movement that
// keeps the row its latest import read.
export const KIND_ORDER = [
  'sign', 'amount', 'date', 'description', 'new', 'gone', 'leftover', 'balance', 'housekeeping', 'other',
];
export const READ_DIFFERENTLY = ['sign', 'amount', 'date', 'description'];

// A group longer than this shows its first rows and a "Show the other N".
export const PREVIEW_ROWS = 3;
export const COLLAPSE_AFTER = 5;

export const itemKind = (item) => (KIND_ORDER.includes(item.change?.kind) ? item.change.kind : 'other');
export const isQuestion = (item) => item.kind === 'decision';
export const isNote = (item) => item.kind === 'informational';

// An item the Owner can still decide: not applied, undone or set aside.
const OPEN = ['pending', 'accepted', 'rejected'];
export const isOpen = (item) => OPEN.includes(item.status) && !isNote(item);

/**
 * The statements the checkup walks: those that need a look, newest upload
 * first. `flags` is `GET /repairs/statements` by source file id.
 */
export function checkupQueue(flags) {
  return Object.values(flags || {})
    .filter((row) => row.needs_attention)
    .map((row) => row.source_file_id)
    .sort((a, b) => b - a);
}

// The statement after `current` that is not done or skipped, or null.
export function nextInQueue(queue, current, settled = []) {
  const start = queue.indexOf(current);
  const rest = [...queue.slice(start + 1), ...queue.slice(0, Math.max(start, 0))];
  return rest.find((id) => id !== current && !settled.includes(id)) ?? null;
}

// How a statement's check starts: read it again whenever its page text is
// kept, since a statement is judged by how today's reader reads it; without
// the text, what is stored is checked.
export const shouldReread = (flags) => Boolean(flags?.rereadable);

// The banner's counts, from every statement's flags.
export function checkupCounts(flags) {
  const rows = Object.values(flags || {});
  return {
    total: rows.length,
    attention: rows.filter((row) => row.needs_attention).length,
    doesntAddUp: rows.filter((row) => row.adds_up === false && !row.balance_matches).length,
    reprocess: rows.filter((row) => row.reprocess_suggested).length,
    leftovers: rows.filter((row) => row.leftover_rows > 0).length,
    fixed: rows.filter((row) => row.repaired).length,
  };
}

/**
 * The items as groups of one kind each, in `KIND_ORDER`, items kept in plan
 * order. Notes (items that propose nothing) are left out: the review shows
 * them apart.
 */
export function groupChanges(items) {
  const byKind = {};
  (items || []).forEach((item) => {
    if (isNote(item)) return;
    const kind = itemKind(item);
    (byKind[kind] = byKind[kind] || []).push(item);
  });
  return KIND_ORDER.filter((kind) => byKind[kind]).map((kind) => ({ kind, items: byKind[kind] }));
}

/**
 * What each item starts as: `true` to change, `false` to leave it, `null` for
 * a question not answered yet. What the server already holds wins; otherwise
 * a safe proposal is selected and anything else is not.
 */
export function initialChoices(items) {
  const choices = {};
  (items || []).forEach((item) => {
    if (!isOpen(item)) return;
    if (item.status === 'accepted') choices[item.id] = true;
    else if (item.status === 'rejected') choices[item.id] = false;
    else if (isQuestion(item)) choices[item.id] = null;
    else choices[item.id] = Boolean(item.safe);
  });
  return choices;
}

/**
 * The calls Apply makes before applying: accept what is chosen and not yet
 * accepted; reject what was accepted and is no longer chosen, and a question
 * answered "leave it", so the answer is remembered. A proposal left
 * unselected stays pending.
 */
export function decisionsToSend(items, choices) {
  const accept = [];
  const reject = [];
  (items || []).forEach((item) => {
    if (!isOpen(item)) return;
    const choice = choices[item.id];
    if (choice === true && item.status !== 'accepted') accept.push(item.id);
    if (choice === false && (item.status === 'accepted' || (isQuestion(item) && item.status === 'pending'))) {
      reject.push(item.id);
    }
  });
  return { accept, reject };
}

// Questions this many alike are answered together, and one by one on request.
export const BULK_MIN = 3;

// A line param that reads the same whichever movements a question names:
// no id and no movement or row card.
const isPlainParam = ([key, value]) =>
  !/(^|_)ids?$/.test(key) && (value === null || typeof value !== 'object' || Array.isArray(value));

// Whether a line names nothing of its own, so it reads the same on every
// question of a shape.
export const namesNothing = (line) => Object.entries(line.params || {}).every(isPlainParam);

/**
 * What a question says, without the movements it names: its kind, its class
 * and every line of it with its params minus ids and movement cards.
 * Questions with one shape say the same thing, so they can take one answer.
 */
export function questionShape(item) {
  const plain = (params) => Object.entries(params || {}).filter(isPlainParam)
    .sort(([a], [b]) => a.localeCompare(b));
  const lines = (item.keep_lose_gain?.lines || []).map((line) => [line.section, line.code, plain(line.params)]);
  return JSON.stringify([itemKind(item), item.defect_class, lines]);
}

// The open questions that share a shape, at least `min` of them each, in plan order.
export function questionShapes(items, min = BULK_MIN) {
  const byShape = new Map();
  (items || []).forEach((item) => {
    if (!isOpen(item) || !isQuestion(item)) return;
    const shape = questionShape(item);
    byShape.set(shape, [...(byShape.get(shape) || []), item]);
  });
  return [...byShape.entries()]
    .filter(([, group]) => group.length >= min)
    .map(([shape, group]) => ({ shape, items: group }));
}

// One answer for all of them, `null` while none is answered, or 'mixed'.
export function sharedAnswer(items, choices) {
  const answers = new Set(items.map((item) => choices[item.id] ?? null));
  return answers.size === 1 ? [...answers][0] : 'mixed';
}

export const readyCount = (items, choices) =>
  (items || []).filter((item) => isOpen(item) && choices[item.id] === true).length;

export const openQuestions = (items, choices) =>
  (items || []).filter((item) => isOpen(item) && isQuestion(item) && choices[item.id] == null);

// The money a set of items moves, per currency, as positive amounts.
export function movedByCurrency(items) {
  const moved = {};
  (items || []).forEach((item) => (item.expected_delta || []).forEach(({ currency, amount }) => {
    moved[currency] = (moved[currency] || 0) + Math.abs(Number(amount) || 0);
  }));
  return Object.entries(moved).filter(([, amount]) => amount > 0);
}

const hasBalances = (side) => side.opening != null && side.closing != null;

// A statement's own arithmetic, per account and currency, for the equation.
// `printed` is false when no reading of it has an opening and a closing: an
// import may have missed balances a re-read finds.
export function statementSums(diff) {
  return (diff?.accounts || []).map((account) => {
    const side = (key) => ({
      opening: account.balances?.opening?.[key] ?? null,
      rows: account.rows_total?.[key] ?? null,
      closing: account.balances?.closing?.[key] ?? null,
      residual: account.residual?.[key] ?? null,
      addsUp: account.adds_up?.[key] ?? null,
      rounded: Boolean(account.rounded?.[key]),
    });
    const before = side('before');
    const after = side('after');
    return {
      accountId: account.account_id,
      currency: account.currency,
      before,
      after: diff.reread ? after : null,
      printed: hasBalances(before) || (Boolean(diff.reread) && hasBalances(after)),
      counts: account.counts || {},
      newRows: account.new || [],
    };
  });
}

// How many rows the statement has and how many a re-read changes.
export function rowCounts(diff) {
  const total = { same: 0, changed: 0, new: 0, gone: 0 };
  (diff?.accounts || []).forEach((account) => Object.keys(total).forEach((key) => {
    total[key] += account.counts?.[key] || 0;
  }));
  return {
    ...total,
    all: total.same + total.changed + total.new + total.gone,
    different: total.changed + total.new + total.gone,
  };
}

// The largest miss of a statement's arithmetic, for its headline: the
// residual with the biggest size, on either side, where the rows do not add
// up. A cent, or the bank's round-up of what is owed, adds up.
export function largestResidual(sums, key = 'before') {
  let found = null;
  sums.forEach((entry) => {
    const side = entry[key];
    if (!side || side.residual == null || side.addsUp !== false) return;
    if (!found || Math.abs(side.residual) > Math.abs(found.amount)) {
      found = { amount: side.residual, currency: entry.currency };
    }
  });
  return found;
}

/**
 * The verdict a review opens with, as a key of `repairs:checkup.verdict` and
 * its values. `run` is the check's run, with its diff.
 */
export function verdictOf(run) {
  const diff = run?.statement_diff;
  const sums = statementSums(diff);
  const before = largestResidual(sums, 'before');
  if (diff?.reread) {
    const after = largestResidual(sums, 'after');
    const addsUpAfter = sums.every((entry) => entry.after?.addsUp !== false);
    if (before) return { key: addsUpAfter && !after ? 'offFixed' : 'offStill', amount: before };
    if (after) return { key: 'offReread', amount: after };
    const changed = rowCounts(diff).different;
    if (changed) return { key: 'readDifferently', count: changed };
  } else if (before) {
    return { key: 'offNoReread', amount: before };
  }
  const open = (run?.items || []).filter((item) => isOpen(item));
  const leftovers = open.filter((item) => itemKind(item) === 'leftover').length;
  if (leftovers && leftovers === open.length) return { key: 'leftovers', count: leftovers };
  return { key: open.length ? 'changes' : 'nothing' };
}

/**
 * The movements whose warnings the given changes clear, counted once: each
 * change's preview says which of its movements raised a warning before and
 * none after (`warnings_cleared`).
 */
export function warningsCleared(items) {
  return new Set((items || []).flatMap((item) => item.warnings_cleared || [])).size;
}

/**
 * A statement read twice, as its review shows it: the extra readings an
 * earlier import left, every one of them still to decide, and how many
 * warnings removing them clears. `null` when the check holds anything else.
 */
export function readTwice(run) {
  if (verdictOf(run).key !== 'leftovers') return null;
  const items = (run?.items || []).filter((item) => isOpen(item) && itemKind(item) === 'leftover');
  return { items, warnings: warningsCleared(items) };
}

// Every one of the given changes chosen: one decision for all of them.
export const chooseAll = (choices, items) => ({
  ...choices,
  ...Object.fromEntries((items || []).map((item) => [item.id, true])),
});

/**
 * What a check applied, for the "Fixed" screen: the rows it checked, those
 * already right, and its applied items counted by what they did.
 * `readTwice` is a check that only removed extra readings; `warningsCleared`
 * counts the movements whose warnings its changes cleared.
 */
export function appliedSummary(run) {
  const applied = (run?.items || []).filter((item) => item.status === 'applied');
  const count = (kinds) => applied.filter((item) => kinds.includes(itemKind(item))).length;
  const rows = rowCounts(run?.statement_diff);
  const added = applied
    .filter((item) => itemKind(item) === 'new')
    .reduce((sum, item) => sum + (item.change?.rows || 1), 0);
  return {
    applied,
    checked: rows.all,
    alreadyRight: rows.same,
    changed: count(READ_DIFFERENTLY),
    added,
    removed: count(['gone']),
    other: count(['leftover', 'balance', 'housekeeping', 'other']),
    blocked: (run?.items || []).filter((item) => item.status === 'blocked').length,
    readTwice: applied.length > 0 && applied.every((item) => itemKind(item) === 'leftover'),
    warningsCleared: warningsCleared(applied),
  };
}
